import type { Graphics } from 'pixi.js';
import { GLYPH_INK } from './card-glyphs-combat';

/**
 * Defense / mobility / economy glyphs (docs/03 W4). The old look-alikes got
 * their own silhouettes: start armor = stacked plates, bulwark = brick wall,
 * salvager = wrench, hit guard = hourglass, speed = fast-forward, dash
 * cooldown = stopwatch, ghost dash = a little ghost.
 */

/** Pointy armor plate (same outline as the HUD/header armor chip). */
function plate(g: Graphics, x: number, y: number, s: number): Graphics {
  return g.poly([
    x, y - 13 * s, x + 11 * s, y - 7 * s, x + 11 * s, y + 6 * s,
    x, y + 13 * s, x - 11 * s, y + 6 * s, x - 11 * s, y - 7 * s,
  ]);
}

export function drawSupportGlyph(g: Graphics, icon: string, c: number): boolean {
  switch (icon) {
    case 'heart':
      g.moveTo(0, 14)
        .bezierCurveTo(-20, 0, -12, -14, 0, -5)
        .bezierCurveTo(12, -14, 20, 0, 0, 14)
        .fill(c);
      return true;
    case 'regen': // repair cross with orbiting nanite dots
      g.roundRect(-4, -14, 8, 28, 3).fill(c);
      g.roundRect(-14, -4, 28, 8, 3).fill(c);
      g.circle(-12, -12, 3).fill({ color: c, alpha: 0.7 });
      g.circle(13, 11, 3).fill({ color: c, alpha: 0.7 });
      return true;
    case 'lifesteal': // droplet drawn into a fang
      g.moveTo(0, -16)
        .bezierCurveTo(11, -2, 13, 8, 0, 16)
        .bezierCurveTo(-13, 8, -11, -2, 0, -16)
        .fill(c);
      g.poly([-3, -4, 3, -4, 0, 6]).fill(GLYPH_INK);
      return true;
    case 'shieldstart': // two stacked armor plates — armor ready at the start
      plate(g, -5, -3, 1).stroke({ color: c, width: 3, join: 'round' });
      plate(g, 5, 4, 1).fill(c);
      plate(g, 5, 4, 0.45).fill(GLYPH_INK);
      return true;
    case 'bulwark': // brick wall — flat damage reduction
      for (const [y, xs] of [
        [-14, [[-17, -1], [1, 17]]],
        [-4, [[-17, -9], [-7, 7], [9, 17]]],
        [6, [[-17, -1], [1, 17]]],
      ] as const) {
        for (const [x0, x1] of xs) g.roundRect(x0, y, x1 - x0, 8, 1.5).fill(c);
      }
      return true;
    case 'aegis': // crest shield fused with a power chevron
      g.poly([0, -16, 13, -10, 13, 2, 0, 16, -13, 2, -13, -10]).fill(c);
      g.poly([-7, 3, 0, -6, 7, 3, 4, 6, 0, 1, -4, 6]).fill(GLYPH_INK);
      return true;
    case 'salvager': // wrench — armor salvaged from boss wrecks
      g.moveTo(-13, 13).lineTo(3, -3).stroke({ color: c, width: 8, cap: 'round' });
      g.circle(7, -7, 10).fill(c);
      g.poly([6, -11, 11, -6, 19, -14, 14, -19]).fill(GLYPH_INK);
      g.circle(-13, 13, 1.8).fill(GLYPH_INK);
      return true;
    case 'iframes': // hourglass — longer invulnerability after a hit
      g.roundRect(-13, -18, 26, 4, 2).fill(c);
      g.roundRect(-13, 14, 26, 4, 2).fill(c);
      g.poly([-10, -14, 10, -14, 2, 0, 10, 14, -10, 14, -2, 0]).fill(c);
      g.poly([-5, -11, 5, -11, 0, -5]).fill(GLYPH_INK);
      g.poly([0, 6, 6, 11, -6, 11]).fill(GLYPH_INK);
      return true;
    case 'speed': // fast-forward — move speed
      g.poly([-17, -12, -1, 0, -17, 12]).fill(c);
      g.poly([0, -12, 16, 0, 0, 12]).fill(c);
      return true;
    case 'dashcd': // stopwatch — dash recharges faster
      g.roundRect(-4, -18, 8, 5, 2).fill(c);
      g.circle(0, 3, 14).fill(c);
      g.circle(0, 3, 9).fill(GLYPH_INK);
      g.moveTo(0, 3).lineTo(5, -2).stroke({ color: c, width: 3, cap: 'round' });
      return true;
    case 'ghostdash': // little ghost with motion streaks — dash i-frames
      g.moveTo(-10, 14).arc(1, -2, 11, Math.PI, 0)
        .lineTo(12, 14).lineTo(8, 10).lineTo(5, 14).lineTo(1, 10).lineTo(-3, 14)
        .lineTo(-7, 10).closePath().fill(c);
      g.circle(-3, -2, 2.5).fill(GLYPH_INK);
      g.circle(5, -2, 2.5).fill(GLYPH_INK);
      g.roundRect(-18, -6, 7, 3, 1.5).fill({ color: c, alpha: 0.6 });
      g.roundRect(-17, 2, 6, 3, 1.5).fill({ color: c, alpha: 0.4 });
      return true;
    case 'magnet': // horseshoe magnet
      g.roundRect(-14, -14, 10, 28, 5).fill(c);
      g.roundRect(4, -14, 10, 28, 5).fill(c);
      g.roundRect(-14, -16, 28, 10, 5).fill(c);
      g.roundRect(-12, 6, 6, 8, 2).fill(GLYPH_INK);
      g.roundRect(6, 6, 6, 8, 2).fill(GLYPH_INK);
      return true;
    case 'greed': // faceted gem
      g.poly([0, -14, 12, -4, 0, 16, -12, -4]).fill(c);
      g.moveTo(-12, -4).lineTo(12, -4).stroke({ color: GLYPH_INK, width: 2, alpha: 0.5 });
      g.moveTo(0, -14).lineTo(0, 16).stroke({ color: GLYPH_INK, width: 2, alpha: 0.35 });
      return true;
    case 'discount': // percent sign
      g.circle(-8, -8, 5).stroke({ color: c, width: 4 });
      g.circle(8, 8, 5).stroke({ color: c, width: 4 });
      g.moveTo(10, -13).lineTo(-10, 13).stroke({ color: c, width: 4, cap: 'round' });
      return true;
    case 'rerolldiscount': // cycle arrow wrapped around a percent mark
      g.arc(0, 0, 13, -Math.PI * 0.75, Math.PI * 0.55)
        .stroke({ color: c, width: 4, cap: 'round' });
      g.poly([6, -17, 15, -10, 3, -6]).fill(c);
      g.circle(-4, -4, 3).stroke({ color: c, width: 3 });
      g.circle(4, 4, 3).stroke({ color: c, width: 3 });
      g.moveTo(6, -7).lineTo(-6, 7).stroke({ color: c, width: 3, cap: 'round' });
      return true;
    case 'luckychip': // four-leaf chip
      for (const [dx, dy] of [[-7, -7], [7, -7], [-7, 7], [7, 7]] as const) {
        g.circle(dx, dy, 7).fill(c);
      }
      g.circle(0, 0, 4).fill(GLYPH_INK);
      return true;
    case 'cargo': // crate with lid seam + handle
      g.roundRect(-14, -12, 28, 24, 4).fill(c);
      g.moveTo(-14, -3).lineTo(14, -3).stroke({ color: GLYPH_INK, width: 3 });
      g.roundRect(-5, -9, 10, 4, 2).fill(GLYPH_INK);
      return true;
  }
  return false;
}
